import * as Dialog from '@radix-ui/react-dialog';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { AlertTriangle } from './icons';

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** danger = red confirm button, focus starts on Cancel */
  tone?: 'default' | 'danger';
}

/** The standard unsaved-changes prompt, shared by Modal, useUnsavedChangesGuard and page forms. */
export const DISCARD_CHANGES: ConfirmOptions = {
  title: 'Discard changes?',
  description: 'You have unsaved changes. They will be lost if you close.',
  confirmLabel: 'Discard',
  cancelLabel: 'Keep editing',
  tone: 'danger',
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface Pending extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

/**
 * Promise-based replacement for window.confirm:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Delete project?', tone: 'danger' }))) return;
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  // Return focus to whatever asked the question (there is no Dialog.Trigger).
  const openerRef = useRef<HTMLElement | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setPending((current) => {
        // Only one at a time; a second call while open resolves the first as cancelled.
        current?.resolve(false);
        return { ...options, resolve };
      });
    });
  }, []);

  const settle = (value: boolean) => {
    setPending((current) => {
      current?.resolve(value);
      return null;
    });
  };

  const value = useMemo(() => confirm, [confirm]);
  const danger = pending?.tone === 'danger';

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog.Root open={pending !== null} onOpenChange={(open) => !open && settle(false)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/50" />
          <Dialog.Content
            role="alertdialog"
            onOpenAutoFocus={(e) => {
              openerRef.current = document.activeElement as HTMLElement | null;
              e.preventDefault();
              (danger ? cancelRef : confirmRef).current?.focus();
            }}
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              openerRef.current?.focus?.();
            }}
            className="fixed left-1/2 top-1/2 z-[70] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-xl focus:outline-none dark:bg-gray-800"
          >
            <div className="flex gap-3">
              {danger && <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-red-500" />}
              <div className="min-w-0 flex-1">
                <Dialog.Title className="text-base font-semibold text-gray-900 dark:text-gray-100">{pending?.title}</Dialog.Title>
                {pending?.description && (
                  <Dialog.Description className="mt-1 text-sm text-gray-600 dark:text-gray-300">{pending.description}</Dialog.Description>
                )}
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button ref={cancelRef} variant="secondary" onClick={() => settle(false)}>
                {pending?.cancelLabel ?? 'Cancel'}
              </Button>
              <Button ref={confirmRef} variant={danger ? 'danger' : 'primary'} onClick={() => settle(true)}>
                {pending?.confirmLabel ?? 'Confirm'}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </ConfirmContext.Provider>
  );
}

const fallback: ConfirmFn = async (options) => {
  // No provider mounted (tests, isolated renders): fall back to the browser dialog.
  return typeof window !== 'undefined' ? window.confirm(options.title) : false;
};

export function useConfirm(): ConfirmFn {
  return useContext(ConfirmContext) ?? fallback;
}
